import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import StepIndicator from "../components/StepIndicator";
import {
    CurrencyEuroIcon,
    Square2StackIcon,
    HomeModernIcon,
    DocumentTextIcon
} from "@heroicons/react/24/outline";

const analyzeSteps = [
    { number: 1, label: 'Dónde y quién eres', path: '/analizarpropiedad/:id/paso1' },
    { number: 2, label: 'Imágenes', path: '/analizarpropiedad/:id/imagenes' },
    { number: 3, label: 'Información básica', path: '/analizarpropiedad/:id/datos' },
    { number: 4, label: 'Recibir informe', path: '/analizarpropiedad/:id/informe' }
];


const InputWithIcon = ({ icon: Icon, label, name, type = 'text', formData, onChange, required, ...rest }) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}{required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon className="h-5 w-5 text-gray-400" />
            </div>
            <input
                type={type}
                name={name}
                value={formData[name]}
                onChange={onChange}
                required={required}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                {...rest}
            />
        </div>
    </div>
);

const AnalyzePropertyData = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [numericId, setNumericId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resolving, setResolving] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        precio: '',
        superficie: '',
        habitaciones: '',
        banos: '',
        descripcion: ''
    });

    useEffect(() => {
        const resolve = async () => {
            try {
                const res = await fetch(
                    `${window.REACT_APP_API_ENDPOINT}/api/wizard/propiedades/by-public-id/${id}`,
                    { credentials: 'include' }
                );
                if (!res.ok) throw new Error('Propiedad no encontrada');
                const data = await res.json();
                setNumericId(data.id);
                // Pre-load existing values if already filled
                setFormData({
                    precio: data.precio ?? '',
                    superficie: data.superficie ?? '',
                    habitaciones: data.habitaciones ?? '',
                    banos: data.banos ?? '',
                    descripcion: data.descripcion ?? ''
                });
            } catch (err) {
                setError('No se pudo cargar la propiedad.');
            } finally {
                setResolving(false);
            }
        };
        resolve();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!numericId) return;

        setLoading(true);
        setError(null);

        try {
            const res = await fetch(
                `${window.REACT_APP_API_ENDPOINT}/api/wizard/propiedades/${numericId}/datos`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        descripcion: formData.descripcion,
                        precio: formData.precio ? Number(formData.precio) : null,
                        superficie: Number(formData.superficie),
                        habitaciones: Number(formData.habitaciones),
                        banos: Number(formData.banos)
                    })
                }
            );

            if (!res.ok) throw new Error('Error al guardar los datos');

            navigate(`/analizarpropiedad/${id}/informe`);
        } catch (err) {
            console.error('Error:', err);
            setError('Error al guardar los datos. Por favor, inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    if (resolving) {
        return (
            <div className="max-w-2xl mx-auto px-4 py-8 text-center text-gray-500">
                Cargando...
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
                <StepIndicator currentStep={3} steps={analyzeSteps} propertyId={id} />

                <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
                    Información básica
                </h1>
                <p className="text-gray-500 text-sm text-center mb-8">
                    Necesitamos estos datos para calcular el coste de reforma y la rentabilidad.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InputWithIcon
                            icon={CurrencyEuroIcon}
                            label="Precio (€)"
                            name="precio"
                            type="number"
                            formData={formData}
                            onChange={handleChange}
                            placeholder="Ej. 150000"
                            min="0"
                            required
                        />
                        <InputWithIcon
                            icon={Square2StackIcon}
                            label="Superficie (m²)"
                            name="superficie"
                            type="number"
                            formData={formData}
                            onChange={handleChange}
                            placeholder="Ej. 80"
                            min="0"
                            required
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InputWithIcon
                            icon={HomeModernIcon}
                            label="Habitaciones"
                            name="habitaciones"
                            type="number"
                            formData={formData}
                            onChange={handleChange}
                            placeholder="Ej. 3"
                            min="0"
                            required
                        />
                        <InputWithIcon
                            icon={HomeModernIcon}
                            label="Baños"
                            name="banos"
                            type="number"
                            formData={formData}
                            onChange={handleChange}
                            placeholder="Ej. 1"
                            min="0"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Descripción <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <div className="absolute top-3 left-3 pointer-events-none">
                                <DocumentTextIcon className="h-5 w-5 text-gray-400" />
                            </div>
                            <textarea
                                name="descripcion"
                                value={formData.descripcion}
                                onChange={handleChange}
                                rows={4}
                                placeholder="Describe el estado actual de la propiedad: antigüedad, estado de instalaciones, reformas previas..."
                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                required
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={loading || !numericId}
                            className="inline-flex items-center justify-center py-3 px-8 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition duration-150"
                        >
                            {loading ? 'Guardando...' : 'Siguiente: Recibir informe →'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AnalyzePropertyData;
